// Piece D of the meal-plan-sharing follow-up: manage the household's own
// templates and browse everyone else's public ones. Deliberately its own
// page rather than folded into the original three-section "Meal Plans"
// hub the design conversation first pictured -- "Shared with us" and "My
// past weeks" already exist and work; this adds public templates as a
// standalone piece instead of reorganizing what's already shipped.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMealPlanner } from "../hooks/MealPlannerContext";
import { weekMealCount } from "../lib/planUtils";
import { DINING_OUT, COLORS, FONT_SIZE, SPACE, RADIUS } from "../lib/constants";
import { templateSnapshotToWeekData, loadMyMealPlanTemplates, loadPublicMealPlanTemplates, updateMealPlanTemplate, deleteMealPlanTemplate } from "../lib/db";
import { WeekPlanPreview } from "../components/WeekPlanPreview";

function templateToPreviewPlan(template, recipes) {
  const recipeTitle = (id) => recipes.find((r) => r.id === id)?.title || "Untitled recipe";
  const meals = [];
  Object.entries(template.days || {}).forEach(([day, slots]) => {
    Object.entries(slots || {}).forEach(([mealSlot, dishes]) => {
      Object.entries(dishes || {}).forEach(([dishType, recipeId]) => {
        if (!recipeId) return;
        if (recipeId === DINING_OUT) {
          meals.push({ day, mealSlot, dishType, isLeftover: false, isDiningOut: true, recipeId: null });
          return;
        }
        meals.push({ day, mealSlot, dishType, isLeftover: false, recipeId, recipeTitle: recipeTitle(recipeId) });
      });
    });
  });
  const items = [];
  (template.snacks || []).forEach((id) => items.push({ listType: "snack", recipeId: id, recipeTitle: recipeTitle(id) }));
  (template.preserves || []).forEach((id) => items.push({ listType: "preserves", recipeId: id, recipeTitle: recipeTitle(id) }));
  (template.medicinal || []).forEach((id) => items.push({ listType: "medicinal", recipeId: id, recipeTitle: recipeTitle(id) }));
  return { weekStart: null, title: template.title, meals, items };
}

export function PublicPlansPage() {
  const { householdId, recipes, weekData, applyPublicTemplateAsCurrentWeek, colors } = useMealPlanner();
  const { card, ink, soft, accent, danger, line } = colors;
  const navigate = useNavigate();
  const [myTemplates, setMyTemplates] = useState([]);
  const [publicTemplates, setPublicTemplates] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [confirmApply, setConfirmApply] = useState(null);
  const [error, setError] = useState("");

  function refresh() {
    return Promise.all([loadMyMealPlanTemplates(householdId), loadPublicMealPlanTemplates()]).then(([mine, pub]) => {
      setMyTemplates(mine);
      setPublicTemplates(pub);
    });
  }

  useEffect(() => {
    let cancelled = false;
    refresh()
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [householdId]);

  async function handleTogglePublic(template) {
    setError("");
    try {
      await updateMealPlanTemplate(template.id, { isPublic: !template.is_public });
      await refresh();
    } catch (e) {
      setError(e.message || "Couldn't update that template.");
    }
  }

  async function handleDelete(template) {
    setError("");
    try {
      await deleteMealPlanTemplate(template.id);
      await refresh();
    } catch (e) {
      setError(e.message || "Couldn't delete that template.");
    }
  }

  function applyTemplate(template) {
    if (weekMealCount(weekData) > 0) {
      setConfirmApply(template.id);
      return;
    }
    doApply(template);
  }

  async function doApply(template) {
    setConfirmApply(null);
    await applyPublicTemplateAsCurrentWeek(templateSnapshotToWeekData(template));
    navigate("/plan");
  }

  if (!loaded) {
    return (
      <div className="mp-sans" style={{ color: soft, padding: SPACE.x3 }}>
        Loading…
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="mp-sans" style={{ color: danger, fontSize: FONT_SIZE.base, marginBottom: SPACE.xl }}>
          {error}
        </div>
      )}

      <div style={{ background: card, border: `1px solid ${line}`, borderRadius: RADIUS.xl, padding: "var(--space-xl2) var(--space-xxl)", marginBottom: SPACE.x3 }}>
        <div style={{ fontWeight: 700, fontSize: FONT_SIZE.lg, marginBottom: SPACE.xs }}>My templates</div>
        <p className="mp-sans" style={{ fontSize: FONT_SIZE.base, color: soft, margin: "0 0 var(--space-xl)" }}>
          Publish a template from the "Publish as template" button on the Our plan tab.
        </p>
        {myTemplates.length === 0 && (
          <p className="mp-sans" style={{ fontSize: FONT_SIZE.base, color: soft, margin: 0 }}>
            You haven't published any templates yet.
          </p>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: SPACE.md }}>
          {myTemplates.map((t) => {
            const isExpanded = expanded === t.id;
            return (
              <div key={t.id} style={{ border: `1px solid ${line}`, borderRadius: RADIUS.lg, padding: "var(--space-lg) var(--space-xl2)" }}>
                <div className="mp-sans" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: SPACE.lg, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: FONT_SIZE.md }}>{t.title}</div>
                    <div style={{ fontSize: FONT_SIZE.sm, color: soft }}>{t.is_public ? "Public" : "Private"}</div>
                  </div>
                  <div style={{ display: "flex", gap: SPACE.md, flexWrap: "wrap" }}>
                    <button className="mp-btn" onClick={() => setExpanded(isExpanded ? null : t.id)} style={{ background: "transparent", border: `1px solid ${line}`, color: accent, padding: "var(--space-xs) var(--space-lg)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.sm }}>
                      {isExpanded ? "Hide" : "Preview"}
                    </button>
                    <button className="mp-btn" onClick={() => handleTogglePublic(t)} style={{ background: "transparent", border: `1px solid ${line}`, color: accent, padding: "var(--space-xs) var(--space-lg)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.sm }}>
                      {t.is_public ? "Make private" : "Make public"}
                    </button>
                    <button
                      className="mp-btn"
                      onClick={() => applyTemplate(t)}
                      style={{ background: accent, color: COLORS.onAccent, padding: "var(--space-xs) var(--space-lg)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.sm }}
                    >
                      Use as template
                    </button>
                    <button className="mp-btn" onClick={() => handleDelete(t)} style={{ background: "transparent", border: `1px solid ${line}`, color: danger, padding: "var(--space-xs) var(--space-lg)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.sm }}>
                      Delete
                    </button>
                  </div>
                </div>
                {confirmApply === t.id && (
                  <div className="mp-sans" style={{ marginTop: SPACE.lg, fontSize: FONT_SIZE.sm, color: danger, display: "flex", alignItems: "center", gap: SPACE.md }}>
                    This week already has meals planned — replace them with this template?
                    <button className="mp-btn" onClick={() => doApply(t)} style={{ background: danger, color: COLORS.onAccent, padding: "var(--space-xs) var(--space-lg)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.sm }}>
                      Replace
                    </button>
                    <button className="mp-btn" onClick={() => setConfirmApply(null)} style={{ background: "transparent", border: `1px solid ${line}`, color: ink, padding: "var(--space-xs) var(--space-lg)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.sm }}>
                      Cancel
                    </button>
                  </div>
                )}
                {isExpanded && (
                  <div style={{ marginTop: SPACE.xl, borderTop: `1px dashed ${line}`, paddingTop: SPACE.xl }}>
                    <WeekPlanPreview plan={templateToPreviewPlan(t, recipes)} colors={colors} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ background: card, border: `1px solid ${line}`, borderRadius: RADIUS.xl, padding: "var(--space-xl2) var(--space-xxl)" }}>
        <div style={{ fontWeight: 700, fontSize: FONT_SIZE.lg, marginBottom: SPACE.xs }}>Public plans</div>
        <p className="mp-sans" style={{ fontSize: FONT_SIZE.base, color: soft, margin: "0 0 var(--space-xl)" }}>
          Templates other households have made public. Every recipe in a public template is itself public, so previews always resolve.
        </p>
        {publicTemplates.length === 0 && (
          <p className="mp-sans" style={{ fontSize: FONT_SIZE.base, color: soft, margin: 0 }}>
            No public templates yet.
          </p>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: SPACE.md }}>
          {publicTemplates.map((t) => {
            const isExpanded = expanded === t.id;
            return (
              <div key={t.id} style={{ border: `1px solid ${line}`, borderRadius: RADIUS.lg, padding: "var(--space-lg) var(--space-xl2)" }}>
                <div className="mp-sans" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: SPACE.lg, flexWrap: "wrap" }}>
                  <div style={{ fontWeight: 700, fontSize: FONT_SIZE.md }}>{t.title}</div>
                  <div style={{ display: "flex", gap: SPACE.md, flexWrap: "wrap" }}>
                    <button className="mp-btn" onClick={() => setExpanded(isExpanded ? null : t.id)} style={{ background: "transparent", border: `1px solid ${line}`, color: accent, padding: "var(--space-xs) var(--space-lg)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.sm }}>
                      {isExpanded ? "Hide" : "Preview"}
                    </button>
                    <button className="mp-btn" onClick={() => applyTemplate(t)} style={{ background: accent, color: COLORS.onAccent, padding: "var(--space-xs) var(--space-lg)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.sm }}>
                      Use as template
                    </button>
                  </div>
                </div>
                {confirmApply === t.id && (
                  <div className="mp-sans" style={{ marginTop: SPACE.lg, fontSize: FONT_SIZE.sm, color: danger, display: "flex", alignItems: "center", gap: SPACE.md }}>
                    This week already has meals planned — replace them with this template?
                    <button className="mp-btn" onClick={() => doApply(t)} style={{ background: danger, color: COLORS.onAccent, padding: "var(--space-xs) var(--space-lg)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.sm }}>
                      Replace
                    </button>
                    <button className="mp-btn" onClick={() => setConfirmApply(null)} style={{ background: "transparent", border: `1px solid ${line}`, color: ink, padding: "var(--space-xs) var(--space-lg)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.sm }}>
                      Cancel
                    </button>
                  </div>
                )}
                {isExpanded && (
                  <div style={{ marginTop: SPACE.xl, borderTop: `1px dashed ${line}`, paddingTop: SPACE.xl }}>
                    <WeekPlanPreview plan={templateToPreviewPlan(t, recipes)} colors={colors} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
